import { useState } from 'react';
import type { ForgeScoreResult } from '../../lib/forgeScore';
import { computeBrushClearance, computeLaunchVelocityFps } from '../../lib/trajectory';
import type { Build, Environment } from '../../lib/trajectory/types';
import { MeasureTuneModal } from '../measure/MeasureTuneModal';

/** Format a number for display, degrading a non-finite value to an em dash
 *  instead of printing "NaN" (defense-in-depth for a non-physical build). */
function fmt(n: number, digits: number, suffix = ''): string {
  return Number.isFinite(n) ? `${n.toFixed(digits)}${suffix}` : '—';
}

interface ScorePanelProps {
  label: string;
  color: 'ember' | 'cyan';
  score: ForgeScoreResult;
  env: Environment;
  build: Build;
  onBuildChange?: (patch: Partial<Build>) => void;
}

export function ScorePanel({ label, color, score, env, build, onBuildChange }: ScorePanelProps) {
  const brush = computeBrushClearance(score.trajectory, env);
  const launchFps = computeLaunchVelocityFps(build);
  const isMeasured = build.measuredVelocityFps != null && build.measuredVelocityFps > 0;
  const [showMeasure, setShowMeasure] = useState(false);
  return (
    <div className={`score-panel score-panel-${color}`}>
      <h3>{label}</h3>
      <div className="score-headline">
        <a
          className="score-number"
          href="/pages/methods#forgescore"
          target="_blank"
          rel="noopener"
          title="How the Forge Score is calculated"
        >
          {score.score}
        </a>
        <span className="score-tier">{score.tier}</span>
      </div>
      <div className="score-components">
        <Bar
          label="Tune quality"
          value={score.components.tuneQuality}
          weight={score.effectiveWeights.tuneQuality}
        />
        <Bar
          label="Momentum"
          value={score.components.momentum}
          weight={score.effectiveWeights.momentum}
        />
        <Bar
          label="Effective FOC"
          value={score.components.effectiveFocPenetration}
          weight={score.effectiveWeights.effectiveFocPenetration}
        />
        <Bar
          label="Energy retention"
          value={score.components.energyRetention}
          weight={score.effectiveWeights.energyRetention}
        />
      </div>
      <div className="score-trajectory">
        <Row
          label="Launch fps"
          value={`${fmt(launchFps, 0)} ${isMeasured ? '(chrono)' : '(computed)'}`}
        />
        <Row label="Drop" value={fmt(score.trajectory.dropIn, 1, '"')} />
        <Row label="Drift" value={fmt(score.trajectory.driftIn, 1, '"')} />
        <Row label="KE @ impact" value={fmt(score.trajectory.keAtImpactFtLb, 0, ' ft-lb')} />
        <Row label="TOF" value={fmt(score.trajectory.tofMs, 0, ' ms')} />
        <Row label="Impact yaw" value={fmt(score.trajectory.impactYawDeg, 1, '°')} />
        <Row
          label={`Apex (${fmt(score.trajectory.apexRangeYards, 0)}y)`}
          value={`${fmt(score.trajectory.apexHeightIn, 1, '"')} above sight line`}
        />
      </div>
      {brush.enabled && (
        <div className={`brush-clearance ${brush.clears ? 'clears' : 'blocked'}`}>
          {brush.clears
            ? `✓ Clears brush at ${brush.brushAtYards}y by ${brush.clearanceMarginIn.toFixed(1)}"`
            : `✗ HITS brush at ${brush.brushAtYards}y by ${Math.abs(brush.clearanceMarginIn).toFixed(1)}"`}
          <div className="brush-detail">
            Arrow at {brush.arrowHeightAtBrushIn.toFixed(1)}" / brush at {brush.brushHeightIn}"
          </div>
        </div>
      )}
      <p className="score-diagnostic">
        {score.diagnostic}
      </p>

      <details className="energy-budget">
        <summary>Energy budget ({score.energyBudget.effectiveKeAtImpactFtLb} ft-lb effective)</summary>
        <div className="budget-rows">
          <BudgetRow label="Launch KE" value={score.energyBudget.launchKeFtLb} positive />
          <BudgetRow label="− Drag loss in flight" value={score.energyBudget.dragLossFtLb} />
          <BudgetRow label="− Tune yaw shearing" value={score.energyBudget.tuneYawLossFtLb} />
          <BudgetRow label="− Wind shear at impact" value={score.energyBudget.windShearLossFtLb} />
          <BudgetRow label="− Deflection risk" value={score.energyBudget.deflectionRiskLossFtLb} />
          <BudgetRow label="= Effective at impact" value={score.energyBudget.effectiveKeAtImpactFtLb} positive bold />
        </div>
      </details>

      <p className="score-source-note">
        Source:{' '}
        {score.source === 'opencv'
          ? 'Measured from logged photos / bareshaft'
          : score.source === 'slider'
          ? 'Estimated from slider input'
          : 'Assumed clean — measure your tune for an accurate score'}
        {onBuildChange && (
          <button
            type="button"
            className="measure-link"
            onClick={() => setShowMeasure(true)}
          >
            Measure tune →
          </button>
        )}
      </p>

      {showMeasure && onBuildChange && (
        <MeasureTuneModal
          build={build}
          onApply={(patch) => onBuildChange(patch)}
          onClose={() => setShowMeasure(false)}
        />
      )}
    </div>
  );
}

const BAR_EXPLAINS: Record<string, string> = {
  'Tune quality': "Driven by measured impact yaw (OpenCV photo detection) or slider input. Below 2° = 100%; above 12° = severe penalty. The dominant axis — nothing else matters if the arrow is fighting itself at impact.",
  'Momentum': "Mass × velocity, the variable that does the most penetration work on big game once tune is held constant. We use momentum instead of KE because momentum is conservative across the whole flight.",
  'Effective FOC': "Not raw FOC — the diminishing-returns curve that turns FOC's geometric benefit into a measurable score, with the deflection-probability matrix applied per shot scenario. Above 15% FOC the marginal benefit gets small.",
  'Energy retention': "What the build still has at the range you selected, not what it had at the chronograph. Prevents the score from over-rewarding light-fast builds for ranges they can't carry to.",
};

function Bar({ label, value, weight }: { label: string; value: number; weight: number }) {
  return (
    <div className="score-bar" title={BAR_EXPLAINS[label]}>
      <div className="score-bar-label">
        {label} <span className="score-bar-weight">({weight}%)</span>
      </div>
      <div className="score-bar-track">
        <div className="score-bar-fill" style={{ width: `${Math.min(100, value)}%` }} />
      </div>
      <span className="score-bar-value">{value}</span>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="score-row">
      <span className="score-row-label">{label}</span>
      <span className="score-row-value">{value}</span>
    </div>
  );
}

function BudgetRow({ label, value, positive, bold }: { label: string; value: number; positive?: boolean; bold?: boolean }) {
  return (
    <div className={`budget-row ${bold ? 'bold' : ''} ${positive ? 'positive' : 'negative'}`}>
      <span className="budget-row-label">{label}</span>
      <span className="budget-row-value">{value.toFixed(1)} ft-lb</span>
    </div>
  );
}
